import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { ChevronDown, ChevronRight, Clock } from "lucide-react";

function formatDueDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("de-DE");
}

/**
 * Liefert die Anzahl Tage zwischen jetzt und due_date.
 * Negativ = überfällig, 0–30 = bald fällig, >30 = noch entspannt.
 */
function daysUntilDue(iso) {
  if (!iso) return null;
  const due = new Date(iso);
  const now = new Date();
  return Math.floor((due - now) / (1000 * 60 * 60 * 24));
}

/**
 * „Bald fällig"-Marker an einzelner Aufgabe.
 * Erscheint nur, wenn die Aufgabe offen UND innerhalb der nächsten 30 Tage fällig ist.
 */
function SoonDueBadge({ assignment }) {
  if (assignment.status !== "offen") return null;
  const days = daysUntilDue(assignment.due_date);
  if (days === null || days < 0 || days > 30) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-sky-100 text-sky-800"
      data-testid={`badge-bald-faellig-${assignment.id}`}
    >
      <Clock className="w-3 h-3" />
      Bald fällig · {days === 0 ? "heute" : `${days} Tg.`}
    </span>
  );
}

export default function AssignmentBatchRow({ batch }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const {
    batch_id,
    course,
    due_date,
    assignments,
    counts,
  } = batch;

  const isSingle = assignments.length === 1;
  const singleAssignment = isSingle ? assignments[0] : null;

  // Für gruppierte Sammelvorgänge: ein "Bald fällig"-Hinweis an der Sammel-Zeile,
  // wenn die Sammel-Frist nah ist und mindestens 1 offene Aufgabe darin liegt.
  const batchDaysLeft = daysUntilDue(due_date);
  const batchIsSoonDue =
    !isSingle &&
    counts.offen > 0 &&
    batchDaysLeft !== null &&
    batchDaysLeft >= 0 &&
    batchDaysLeft <= 30;

  if (isSingle) {
    return (
      <div
        className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
        data-testid={`assignment-row-${singleAssignment.id}`}
      >
        <div className="flex items-center gap-4 min-w-0">
          {singleAssignment.employee?.photo_url ? (
            <img
              src={singleAssignment.employee.photo_url}
              alt=""
              className="w-10 h-10 rounded-full object-cover bg-slate-100"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-slate-200" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-medium text-slate-900 truncate">
                {course?.title || "Unterweisung"}
              </div>
              <SoonDueBadge assignment={singleAssignment} />
            </div>
            <div className="text-sm text-slate-500 truncate">
              {singleAssignment.employee?.first_name}{" "}
              {singleAssignment.employee?.last_name}
              {due_date && <> · fällig {formatDueDate(due_date)}</>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <StatusBadge
            status={singleAssignment.status}
            testId={`row-status-${singleAssignment.id}`}
            lang="de"
          />
          {singleAssignment.status === "erledigt" &&
          singleAssignment.nachweis_id ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/unternehmer/nachweis/${singleAssignment.nachweis_id}`)
              }
              data-testid={`open-nachweis-${singleAssignment.id}`}
            >
              Nachweis öffnen
            </Button>
          ) : (
            <span className="text-xs text-slate-400">
              Wartet auf Mitarbeiter
            </span>
          )}
        </div>
      </div>
    );
  }

  // Mehrere Empfänger: Sammelvorgang
  return (
    <div data-testid={`assignment-batch-${batch_id}`}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors text-left"
        data-testid={`batch-toggle-${batch_id}`}
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
            {expanded ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-medium text-slate-900 truncate">
                {course?.title || "Unterweisung"}
              </div>
              {batchIsSoonDue && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-sky-100 text-sky-800"
                  data-testid={`batch-bald-faellig-${batch_id}`}
                >
                  <Clock className="w-3 h-3" />
                  Bald fällig · {batchDaysLeft === 0 ? "heute" : `${batchDaysLeft} Tg.`}
                </span>
              )}
            </div>
            <div className="text-sm text-slate-500 truncate">
              {assignments.length} Mitarbeiter
              {due_date && <> · fällig {formatDueDate(due_date)}</>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0 text-xs sm:text-sm">
          {counts.erledigt > 0 && (
            <span
              className="text-emerald-700 font-medium"
              data-testid={`batch-erledigt-${batch_id}`}
            >
              {counts.erledigt} erledigt
            </span>
          )}
          {counts.offen > 0 && (
            <span
              className="text-amber-700 font-medium"
              data-testid={`batch-offen-${batch_id}`}
            >
              {counts.offen} offen
            </span>
          )}
          {counts.ueberfaellig > 0 && (
            <span
              className="text-red-700 font-medium"
              data-testid={`batch-ueberfaellig-${batch_id}`}
            >
              {counts.ueberfaellig} überfällig
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="bg-slate-50/70 border-t border-slate-100 divide-y divide-slate-100">
          {assignments.map((a) => (
            <div
              key={a.id}
              className="px-6 py-3 pl-20 flex items-center justify-between gap-4"
              data-testid={`batch-detail-${a.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {a.employee?.photo_url ? (
                  <img
                    src={a.employee.photo_url}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover bg-slate-100"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-slate-200" />
                )}
                <span className="text-sm text-slate-700 truncate">
                  {a.employee?.first_name} {a.employee?.last_name}
                </span>
                <SoonDueBadge assignment={a} />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={a.status} testId={`row-status-${a.id}`} lang="de" />
                {a.status === "erledigt" && a.nachweis_id ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigate(`/unternehmer/nachweis/${a.nachweis_id}`)
                    }
                    data-testid={`open-nachweis-${a.id}`}
                    className="text-xs"
                  >
                    Nachweis
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
